<template>
	<div :class="offerBoxClass">
		<Loading
			v-if="isLoading"
			class="offer-box__loading"
		/>
		<template v-else>
			<div
				v-if="hasBestOffer"
				class="offer-box__content-container"
			>
				<div class="offer-box__title-feature-container">
					<div class="offer-box__title">
						<h2>{{ offerType }}</h2>
					</div>
					<div
						v-for="feature in features"
						:key="feature.label"
						class="offer-box__feature"
					>
						<BaseIcon
							:name="'check'"
							class="offer-box__list-icon offer-box__list-icon--available"
						/>
						<div :class="feature.class">
							<span>{{ normalizeHotelAttributes(feature.label) }}</span>
						</div>
					</div>
				</div>
				<div class="offer-box__flight-price-container">
					<div class="offer-box__flight-info">
						<div class="offer-box__flight-icon">
							<BaseIcon
								v-if="offerTransport === 'inkl. Flug'"
								name="planeNew"
								class="offer-box__icon"
							/>
							<BaseIcon
								v-if="offerTransport === 'eigene Anreise'"
								name="room"
								class="offer-box__icon"
							/>
						</div>
						<div class="offer-box__flight-text">
							<h3>{{ offerTransport }}</h3>
						</div>
					</div>

					<BasePrice
						v-if="bestOffer?.CurrencyCode && bestOffer?.Pricing"
						class="offer-box__flight-price"
						:theme="'accent'"
						prefix="ab"
						:price="totalPrice"
						:currency="bestOffer.CurrencyCode"
					/>
					<div class="offer-box__additional-info">
						<span>{{ fieldLabel }} &centerdot; {{ duration }}</span>
					</div>
				</div>
			</div>
			<div
				v-if="!hasBestOffer"
				class="offer-box__content-no-offer"
			>
				<div class="offer-box__title-no-offer">
					<h2>{{ NO_OFFER_TITLE }}</h2>
				</div>
				<div
					v-if="isPackage"
					class="offer-box__text-no-offer"
				>
					<p>{{ NO_OFFER_MESSAGE_PACKAGE }}</p>
				</div>
				<div
					v-else-if="!isPackage"
					class="offer-box__text-no-offer"
				>
					<p>{{ NO_OFFER_MESSAGE_NO_PACKAGE }}</p>
				</div>
			</div>

			<div class="offer-box__button-container">
				<RoundedButton
					v-if="hasBestOffer"
					class="offer-box__button"
					theme="extra"
					as="a"
					:href="allOffersHref"
					@click.stop.prevent="emit('showOffersClick', $event)"
				>
					{{ BUTTON_TEXT_ALL_OFFERS }}
				</RoundedButton>
				<RoundedButton
					v-else-if="!hasBestOffer && !isLoading"
					class="offer-box__button"
					theme="extra"
					@click.stop="emit('searchUpdateClick')"
				>
					{{ BUTTON_TEXT_CHANGE_SEARCH }}
				</RoundedButton>

				<LinkButton
					v-if="isPackage"
					class="offer-box__offer-alternative-button"
					:label="LINK_TEXT_WITHOUT_FLIGHT"
					:with-icon="false"
					@click.stop="showWithoutFlight"
				/>
				<LinkButton
					v-if="!isPackage"
					class="offer-box__offer-alternative-button"
					:label="LINK_TEXT_WITH_FLIGHT"
					:with-icon="false"
					@click.stop="showWithFlight"
				/>
			</div>
		</template>
	</div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import BaseIcon from '@lmt-rpb/BaseIcon/BaseIcon.vue';
import RoundedButton from '@lmt-rpb/RoundedButton/RoundedButton.vue';
import { pluralize } from '@utils/utils';
import { normalizeHotelAttributes } from '@/js/utils/arrayUtils'; // Import the utility function
import { getLocaleString } from '@utils/environmentUtils';
import { getTotalPrice } from '@/js/utils/priceUtils';
import {
	NO_OFFER_TITLE,
	NO_OFFER_MESSAGE_PACKAGE,
	NO_OFFER_MESSAGE_NO_PACKAGE,
	BUTTON_TEXT_ALL_OFFERS,
	BUTTON_TEXT_CHANGE_SEARCH,
	LINK_TEXT_WITHOUT_FLIGHT,
	LINK_TEXT_WITH_FLIGHT,
} from '@global-js/constants';
import LinkButton from '@lmt-rpb/Atoms/LinkButton/LinkButton.vue';
import { useStore } from '@/components/common/store';
import roomTypes from '@/js/data/room-types';
import boardTypes from '@/js/data/board-types';
import BasePrice from '@lmt-rpb/Atoms/BasePrice/BasePrice.vue';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';

interface Props {
	allOffersHref: string;
}

defineProps<Props>();

const emit = defineEmits(['packageSwitchClick', 'showOffersClick', 'searchUpdateClick']);

const store = useStore();

const bestOffer = computed(() => store.state.bestOffer);
const totalPrice = computed(() => getTotalPrice(bestOffer.value?.Pricing ?? {}));
const isLoading = computed(() => store.state.bestOfferLoading);
const hasBestOffer = computed(() => !!bestOffer.value?.Price);
const searchMask = computed(() => store.state.searchMask);
const isPackage = computed(() => !searchMask.value.onlyHotel);
const offerType = computed(() => (isPackage.value ? getLocaleString('package') : 'Hotel'));
const offerTransport = computed(() => (isPackage.value ? 'inkl. Flug' : 'eigene Anreise'));
const roomType = computed(() => bestOffer.value?.Room?.Type || '');
const durationValue = computed(() => bestOffer.value?.duration || '');
const travelers = computed(() => searchMask.value.travelers);
const isDesktop = storeToRefs(useBreakpointStore());
const boardType = computed(
	() => bestOffer.value?.BoardType ?? (searchMask.value.boardTypes.length > 0 ? searchMask.value.boardTypes[0] : '')
);

const duration = computed(() => {
	const unit = ['Tag', 'Tage'] as const;
	return durationValue.value ? `${pluralize(durationValue.value, ...unit)}` : '';
});

const boardLabel = computed(() => {
	const label = boardTypes.find((type: { value: string }) => type.value === boardType.value)?.label;
	return label || '';
});

const room = computed(() => {
	const roomLabel = roomTypes.find((type: { value: string }) => type.value === roomType.value)?.label;
	return roomLabel || bestOffer.value?.Room?.Name || '';
});

const offerBoxClass = computed(() => ({
	'offer-box': true,
	'offer-box--no-offer': !hasBestOffer.value,
}));

const fieldLabel = computed((): string => {
	const adultLabel =
		!isDesktop.value || travelers.value.children.length
			? `${travelers.value.adult} Erw.`
			: pluralize(travelers.value.adult, 'Erwachsener', 'Erwachsene');
	const childLabel = travelers.value.children.length ? `, ${pluralize(travelers.value.children.length, 'Kind', 'Kinder')}` : '';
	return `${adultLabel}${childLabel}`;
});

const features = computed(() => [
	{ label: boardLabel.value, class: 'offer-box__feature-Label' },
	{ label: room.value, class: 'offer-box__feature-room' },
]);

const showWithoutFlight = () => {
	store.commit('searchMask/updateFormData', { onlyHotel: true });
	emit('packageSwitchClick');
};
const showWithFlight = () => {
	store.commit('searchMask/updateFormData', { onlyHotel: false });
	emit('packageSwitchClick');
};
</script>

<style lang="scss" scoped>
.offer-box {
	width: 100%;
	display: flex;
	flex-direction: column;
	min-width: 31rem;
	border: 2px solid $color-lime-light;
	padding: 1.6rem;
	min-height: 22.1rem;
	border-radius: $border-radius-large;
	box-shadow: $box-shadow;

	&__loading {
		margin: auto;
	}

	&--no-offer {
		border-color: $color-search-mask-border;

		h2 {
			text-align: left;
		}
	}

	&__content-no-offer {
		display: flex;
		flex-direction: column;

		h2 {
			margin: 0;
			padding: 0;
			margin-bottom: 0.8rem;
			text-align: left;
			font-weight: $font-weight-semibold;
			font-size: $font-medium-3;
			line-height: 2.4rem;
		}

		p {
			margin-bottom: 1.6rem;
			font-size: $font-small-2;
			line-height: 2.3rem;
		}
	}

	&__title h2 {
		margin: 0;
		padding: 0;
		font-size: $font-medium-3;
		line-height: 2.4rem;
		font-weight: $font-weight-semibold;
		text-align: left;
		margin-bottom: 0.8rem;
	}

	&__flight-info {
		display: flex;
		flex-direction: row;
		margin-bottom: 0.8rem;
		align-self: flex-end;
	}

	&__flight-icon {
		display: flex;
		width: 1.8rem;
		height: 100%;
		margin-right: 0.6rem;
	}

	&__flight-icon svg {
		width: 100%;
		height: 100%;
		fill: $color-black;
	}

	&__flight-text h3 {
		margin: 0;
		padding: 0;
		font-size: $font-small-1;
		line-height: 2.4rem;
		font-weight: $font-weight-semibold;
	}

	&__feature {
		display: flex;
		flex-direction: row;
		align-items: first baseline;
		margin-bottom: 0.3rem;
	}

	&__list-icon {
		width: 1rem;
		height: 1.2rem;
		flex-shrink: 0;
		margin-right: 0.7rem;

		&--available {
			height: 1rem;
			width: 1rem;
			fill: $color-price-text;
		}
	}

	&__feature-Label,
	&__feature-room {
		font-size: $font-small-2;
		line-height: 2.3rem;
	}

	&__content-container {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		margin-bottom: 2.4rem;
	}

	&__title-feature-container {
		display: flex;
		flex-direction: column;
	}

	&__flight-price-container {
		display: flex;
		flex-direction: column;
	}

	&__flight-price {
		font-weight: $font-weight-semibold;
		font-size: $font-large-4;
		line-height: 3.3rem;

		:deep(.base-price__prefix) {
			color: $color-text;
			font-size: $font-small-2;
			line-height: 2.3rem;
			font-weight: $font-weight-regular;
		}
	}

	&__button-container {
		display: flex;
		flex-direction: column;
		margin-left: auto;
		margin-right: auto;
		text-align: center;
		justify-content: space-between;
		align-items: center;
	}

	&__additional-info {
		align-self: flex-end;

		span {
			font-size: $font-small-3;
			line-height: 1.8rem;
			color: $color-text;
		}
	}

	:deep(.button) {
		width: fit-content;
		margin-left: auto;
		margin-right: auto;
		background: $color-primary;
		margin-bottom: 0.8rem;

		&:hover {
			background: $color-primary-l0;
		}
	}
}
</style>
